import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../components/AdminLayout'
import AdminStatsRow from '../components/AdminStatsRow'
import { AdminTable, Column } from '../components/AdminTable'
import AdminDateRangePicker, { IsoRange } from '../components/AdminDateRangePicker'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'

type CompanyRow = {
  key: string
  sn: number
  name: string
  createdAt: string
  createdBy: string
  status: string
  lastFiling: string
}

export default function AdminCompanyManagementPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<CompanyRow[]>([])
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
    recentlyAdded: 0,
    failed: 0,
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [range, setRange] = useState<IsoRange>({})
  const itemsPerPage = 10
  const rangeStart = range.start
  const rangeEnd = range.end

  const columns: Column<CompanyRow>[] = [
    { key: 'sn', label: 'S/N' },
    { key: 'name', label: 'Company Name' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'createdBy', label: 'Created By' },
    {
      key: 'status',
      label: 'Status',
      render: row => (
        <span className='inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100'>
          {row.status}
        </span>
      ),
    },
    { key: 'lastFiling', label: 'Last Filing' },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <button
          onClick={() => navigate(`/admin/companies/${row.key}`)}
          className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'
        >
          View
        </button>
      ),
    },
  ]

  // New date range starts back at page 1.
  useEffect(() => {
    setCurrentPage(1)
  }, [rangeStart, rangeEnd])

  useEffect(() => {
    const loadCompanies = async () => {
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.listCompanies({
          page: currentPage,
          limit: itemsPerPage,
          ...(rangeStart ? { start: rangeStart } : {}),
          ...(rangeEnd ? { end: rangeEnd } : {}),
        })
        const data: any = (response as any)?.data || response
        const items = data?.items || data?.data?.items || data?.data || []
        setRows(
          items.map((item: any, idx: number) => ({
            key: item.id || item._id || `${idx}`,
            sn: (currentPage - 1) * itemsPerPage + idx + 1,
            name: item.companyName || item.name,
            createdAt: item.createdAt
              ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : '--',
            createdBy: item.createdBy?.name || [item.createdBy?.firstName, item.createdBy?.lastName].filter(Boolean).join(' ') || '--',
            status: (item.companyStatus || item.status || 'submitted') as string,
            lastFiling: item.lastFiling?.createdAt
              ? new Date(item.lastFiling.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : '--',
          }))
        )

        setTotalItems(data?.total || 0)
        const s = data?.stats || {}
        setStats({
          total: s.total ?? data?.total ?? 0,
          active: s.active ?? 0,
          suspended: s.suspended ?? 0,
          recentlyAdded: s.recentlyAddedThisMonth ?? 0,
          failed: s.failedFilings ?? 0,
        })
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load companies.')
      } finally {
        setLoading(false)
      }
    }

    void loadCompanies()
  }, [currentPage, rangeStart, rangeEnd])

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>Company Management</h1>
            <p className='text-sm text-gray-500'>Every company registered on BritFiler.</p>
          </div>
          <div className='self-start sm:self-auto'>
            <AdminDateRangePicker onChange={setRange} />
          </div>
        </div>

        <AdminStatsRow
          stats={[
            { label: 'Total Companies', value: stats.total },
            { label: 'Active Companies', value: stats.active },
            { label: 'Suspended Companies', value: stats.suspended },
            { label: 'Recently Added Companies This Month', value: stats.recentlyAdded },
            { label: 'Failed Filings', value: stats.failed },
          ]}
        />

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        <AdminTable<CompanyRow>
          columns={columns}
          data={rows}
          loading={loading}
          emptyMessage='No companies found.'
          searchPlaceholder='Search Company'
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </AdminLayout>
  )
}
